import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import paths from '@src/paths';
import { listNoReplyThreads, markThreadNeedsReply } from '@src/api/clientInbox';
import { useMessage } from '@src/context/common';

dayjs.extend(relativeTime);

const WINDOW_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 30, label: 'Last 30 days' },
];

/**
 * Emails the AI decided need no reply. They never become tasks, so without
 * this list they don't exist on /tasks at all — including the rare one the
 * AI got wrong. "Needs a reply" drafts it and makes the task in one tap.
 */
export default function NoReplyEmailList({ onTaskCreated, onCountChange }) {
  const message = useMessage();
  const [days, setDays] = useState(14);
  const [includeAutomated, setIncludeAutomated] = useState(false);
  const [rows, setRows] = useState([]);
  const [automatedCount, setAutomatedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workingId, setWorkingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    listNoReplyThreads({ days, include_automated: includeAutomated ? 'true' : undefined })
      .then((response) => {
        const data = response.data || {};
        setRows(Array.isArray(data.results) ? data.results : []);
        setAutomatedCount(data.automated_count || 0);
        if (!includeAutomated && onCountChange) onCountChange(data.count || 0);
      })
      .catch((err) => {
        console.error('Failed to load no-reply emails', err);
        setError('Could not load these emails.');
      })
      .finally(() => setLoading(false));
  }, [days, includeAutomated, onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  const handleNeedsReply = useCallback(
    (row) => {
      setWorkingId(row.id);
      markThreadNeedsReply(row.id)
        .then((response) => {
          const data = response.data || {};
          setRows((prev) => prev.filter((r) => r.id !== row.id));
          if (onCountChange && !row.automated) {
            onCountChange((prev) => Math.max(0, (typeof prev === 'number' ? prev : 1) - 1));
          }
          if (data.drafted) {
            message.success('Task created with a draft reply.');
          } else if (data.draft_error) {
            message.warning('Task created, but the draft failed — write the reply on the task.');
          } else {
            message.success('Task created.');
          }
          if (onTaskCreated) onTaskCreated(data.task);
        })
        .catch((err) => {
          console.error('Failed to mark needs reply', err);
          message.error(err?.response?.data?.detail || 'Could not create the task.');
        })
        .finally(() => setWorkingId(null));
    },
    [message, onCountChange, onTaskCreated],
  );

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Typography variant="body2" color="text.secondary">
          The AI decided these don&apos;t need a reply, so they never became tasks. If one does,
          tap <strong>Needs a reply</strong> — it drafts one and adds the task.
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          <TextField
            select
            size="small"
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            SelectProps={{ native: true }}
            sx={{ minWidth: 130 }}
          >
            {WINDOW_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeAutomated}
                onChange={(event) => setIncludeAutomated(event.target.checked)}
              />
            }
            label={
              <Typography variant="body2" noWrap>
                Automated{automatedCount ? ` · ${automatedCount}` : ''}
              </Typography>
            }
          />
        </Stack>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Nothing filtered out in this window.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {rows.map((row) => {
            const working = workingId === row.id;
            return (
              <Paper
                key={row.id}
                variant="outlined"
                sx={{ p: { xs: 1.5, sm: 2 }, opacity: row.automated ? 0.8 : 1 }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                >
                  <Box flex={1} minWidth={0}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="subtitle2" fontWeight={600} noWrap>
                        {row.client_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.received_at ? dayjs(row.received_at).fromNow() : ''}
                      </Typography>
                      {row.reason ? (
                        <Chip size="small" variant="outlined" label={row.reason} />
                      ) : null}
                    </Stack>
                    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }} noWrap>
                      {row.subject || 'No subject'}
                    </Typography>
                    {row.summary ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 0.25,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {row.summary}
                      </Typography>
                    ) : null}
                    {row.from_email ? (
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        From {row.from_email}
                      </Typography>
                    ) : null}
                  </Box>
                  <Stack direction="row" spacing={1} flexShrink={0}>
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      disabled={working}
                      startIcon={
                        working ? <CircularProgress size={14} color="inherit" /> : <ReplyIcon fontSize="small" />
                      }
                      onClick={() => handleNeedsReply(row)}
                      sx={{ textTransform: 'none', flex: { xs: 1, sm: 'none' } }}
                    >
                      {working ? 'Drafting…' : 'Needs a reply'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      component={RouterLink}
                      to={`${paths.clientInbox}?thread=${row.id}`}
                      endIcon={<OpenInNewIcon fontSize="small" />}
                      sx={{ textTransform: 'none' }}
                    >
                      Open
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
